/**
 * Thin fetch wrapper. All API responses are JSON; errors carry a client-safe
 * `{ ok:false, error }`. A 401 means the session is gone — callers surface the
 * login screen rather than blanking the page (the fix for the old reload loop).
 * The parsed error body rides along on ApiError so flows like the matching
 * page's cross-owner 409 can read its structured payload.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    credentials: 'same-origin',
  });
  const body = (await res.json().catch(() => ({}))) as { error?: string } & T;
  if (!res.ok) {
    throw new ApiError(res.status, body?.error ?? `HTTP ${res.status}`, body as Record<string, unknown>);
  }
  return body;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, data?: unknown) =>
    request<T>(path, data == null ? { method: 'POST' } : { method: 'POST', body: JSON.stringify(data) }),
  delete: <T = { ok: boolean }>(path: string) => request<T>(path, { method: 'DELETE' }),
  /**
   * A file the server hands back (the monthly statement CSV): fetched with
   * the session, never through a bare <a href> - an admin-only 403 or a 401
   * would otherwise land as a saved HTML error page named .csv. The
   * filename comes from content-disposition, falling back to `fallbackName`.
   */
  blob: async (path: string, fallbackName: string): Promise<{ blob: Blob; filename: string }> => {
    const res = await fetch(path, { credentials: 'same-origin' });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(res.status, body?.error ?? `HTTP ${res.status}`, body as Record<string, unknown>);
    }
    const disposition = res.headers.get('content-disposition') ?? '';
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    return { blob: await res.blob(), filename: m?.[1] ? decodeURIComponent(m[1]) : fallbackName };
  },
};

/** Hand a fetched blob to the browser as a download, then release the URL. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after the click has been dispatched: revoking synchronously can
  // cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export interface AuthStatus {
  needsSetup: boolean;
  user: string | null;
}
